/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * What the checkout owes, and what it is waiting on.
 *
 * Every number here is a *display* of the price, never its authority. The
 * amount actually charged comes back from the checkout session the backend
 * creates, which freezes it for the session's lifetime; a browser that
 * disagrees with that number is a browser that is wrong.
 */

import { documentKind, isCompleteDocument } from '@/shared/utils/input-masks';
import { isValidCNPJFormat, isValidCPFFormat } from '@/shared/utils/valide-formats';
import type { PlanSegment } from '@/features/pricing';
import type { BillingCycle, PlanWithFrequency } from '../../hooks/use-plans';
import type { TrialStatus } from '../../hooks/use-trial-eligibility';

/** Months in a year, for turning a yearly price back into a monthly one. */
export const MONTHS_PER_YEAR = 12;

/** Months a yearly subscription is billed for: two of the twelve are free. */
export const MONTHS_BILLED_YEARLY = 10;

/** Seats a plan allows when the catalogue does not say. */
const FALLBACK_SEAT_RANGE = { min: 1, max: 1 };

/** The seat range a plan accepts. */
export interface SeatRange {
  min: number;
  max: number;
}

/**
 * The seats a plan will sell, read off the catalogue.
 *
 * Solo reports a range of exactly one, which is what makes the seat stepper
 * disappear on that segment without the component having to know what "solo"
 * means.
 *
 * @param plan - The selected plan.
 * @returns The inclusive seat range.
 */
export function seatRange(plan: PlanWithFrequency | undefined): SeatRange {
  if (!plan) return FALLBACK_SEAT_RANGE;
  const min = plan.minSeats ?? FALLBACK_SEAT_RANGE.min;
  const max = plan.users ?? min;
  return { min, max: Math.max(min, max) };
}

/**
 * Pulls a seat count back inside what the plan allows.
 *
 * Switching to a plan with a lower ceiling has to move the quantity rather
 * than leave the checkout quoting a number the backend will refuse.
 *
 * @param seats - The requested seat count.
 * @param range - The plan's seat range.
 * @returns The seat count the plan accepts.
 */
export function clampSeats(seats: number, range: SeatRange): number {
  if (!Number.isFinite(seats)) return range.min;
  return Math.min(range.max, Math.max(range.min, Math.trunc(seats)));
}

/** Everything the summary needs to state a price. */
export interface PriceInput {
  /**
   * The **undiscounted** monthly price of one seat.
   *
   * Not the yearly plan's monthly equivalent. That figure already has the two
   * free months taken out of it, so running it through the yearly arithmetic
   * below discounts the year twice — three Rubi seats came to R$ 7,475 when
   * the catalogue sells them for R$ 8,970.
   */
  seatPrice: number;
  seats: number;
  cycle: BillingCycle;
}

/** The summary's arithmetic, all of it derived from one seat price. */
export interface PriceBreakdown {
  /** What the seats cost for one month. */
  monthly: number;
  /** What a full twelve months would cost, before the yearly discount. */
  gross: number;
  /** The two months the yearly plan does not bill. */
  discount: number;
  /** What is charged today. */
  total: number;
}

/**
 * Works out the summary's figures.
 *
 * The yearly discount is stated as an explicit line rather than folded into
 * the price, because "you pay ten and get twelve" is the offer and a single
 * discounted total hides it.
 *
 * @param input - The seat price, seat count and cycle.
 * @returns Every figure the summary prints.
 */
export function priceBreakdown({ seatPrice, seats, cycle }: PriceInput): PriceBreakdown {
  const monthly = seatPrice * seats;
  if (cycle !== 'yearly') {
    return { monthly, gross: monthly, discount: 0, total: monthly };
  }
  // Twelve months billed as ten: the gross is what a year at the monthly rate
  // would cost, and the total is what the catalogue actually charges for it.
  const gross = monthly * MONTHS_PER_YEAR;
  const total = monthly * MONTHS_BILLED_YEARLY;
  return { monthly, gross, discount: gross - total, total };
}

/** The fields Payien requires before a session can be created. */
export const REQUIRED_FIELDS = ['name', 'document', 'phone', 'cep', 'number'] as const;

export type RequiredField = (typeof REQUIRED_FIELDS)[number];

/** The checkout's own form values. */
export type CheckoutForm = Record<RequiredField, string>;

/** How many digits a valid document has, per segment. */


/** A CEP is eight digits; a Brazilian phone is ten or eleven. */
export const CEP_DIGITS = 8;
const PHONE_MIN_DIGITS = 10;

/**
 * Counts a field's digits, ignoring whatever the mask put between them.
 *
 * @param value - The masked field value.
 * @returns The digit count.
 */
function digitCount(value: string): number {
  return (value || '').replace(/\D/g, '').length;
}

/** The address the CEP lookup resolves, as Payien needs it. */
export interface ResolvedAddress {
  street: string;
  neighborhood: string;
  city: string;
  state: string;
  codigo_municipio: string;
}

/**
 * The payload that creates the agency behind the subscription.
 *
 * The field names are the API's, not the form's: the form asks for a
 * "document" and a "CEP", and the agency record wants a cpf or a cnpj and a
 * postal_code. Passing the form's own names through created agencies with no
 * document and no address at all, which Payien then refused to invoice.
 *
 * @param form - The form values.
 * @param address - The address the CEP resolved, when it has.
 * @param segment - Which document the segment carries.
 * @returns The agency payload.
 */
export function agencyPayload(
  form: CheckoutForm, address: ResolvedAddress | null, _segment?: PlanSegment,
): Record<string, string> {
  return {
    name: form.name,
    [documentKind(form.document)]: form.document,
    phone: form.phone,
    postal_code: form.cep,
    number: form.number,
    street: address?.street ?? '',
    neighborhood: address?.neighborhood ?? '',
    city: address?.city ?? '',
    state: address?.state ?? '',
    codigo_municipio: address?.codigo_municipio ?? '',
  };
}

/**
 * Whether a document is complete and its check digits add up.
 *
 * Length alone let 123.456.789-00 through to Payien, which refused it only
 * after the card had been typed. The CNPJ check accepts the alphanumeric
 * format, so a 2026 registration is not mistaken for a typo.
 *
 * @param value - The masked document.
 * @returns Whether it is a real CPF or CNPJ.
 */
export function isValidDocument(value: string): boolean {
  if (!isCompleteDocument(value)) return false;
  return documentKind(value) === 'cnpj' ? isValidCNPJFormat(value) : isValidCPFFormat(value);
}

/** The lookup state of the CEP, as far as the form needs to know it. */
export type CepLookup = 'idle' | 'loading' | 'success' | 'not_found' | 'error';

/**
 * Why a field is not ready, as a translation key under `checkoutStep.issues`.
 *
 * `missingFields` says which; this says what is wrong with each, because
 * "confira os 5 campos destacados" sent the buyer looking for the fault
 * instead of telling them what it was.
 *
 * @param field - The field to explain.
 * @param form - The current form values.
 * @param cep - Where the CEP lookup stands.
 * @returns The issue key.
 */
export function fieldIssueKey(field: RequiredField, form: CheckoutForm, cep: CepLookup): string {
  const value = form[field];
  const empty = !value.trim();
  if (field === 'document') {
    if (empty) return 'documentEmpty';
    return isCompleteDocument(value) ? 'documentInvalid' : 'documentIncomplete';
  }
  if (field === 'phone') return empty ? 'phoneEmpty' : 'phoneIncomplete';
  if (field === 'cep') return cepIssueKey(value, cep);
  return field === 'name' ? 'nameEmpty' : 'numberEmpty';
}

/**
 * Why the CEP is not ready.
 *
 * @param value - The masked CEP.
 * @param cep - Where its lookup stands.
 * @returns The issue key.
 */
function cepIssueKey(value: string, cep: CepLookup): string {
  if (digitCount(value) === 0) return 'cepEmpty';
  if (digitCount(value) < CEP_DIGITS) return 'cepIncomplete';
  if (cep === 'loading') return 'cepSearching';
  return cep === 'error' ? 'cepFailed' : 'cepNotFound';
}

/**
 * Which required fields are not yet filled in well enough to pay.
 *
 * Returned in the order the form draws them, because the locked payment box
 * names them back to the customer and a list in field order is a list they can
 * walk up the page.
 *
 * @param form - The current form values.
 * @param segment - The chosen segment, which sets the document length.
 * @returns The unsatisfied field names, in form order.
 */
export function missingFields(
  form: CheckoutForm, segment: PlanSegment, address: ResolvedAddress | null = null,
): RequiredField[] {
  const rules: Record<RequiredField, () => boolean> = {
    name: () => form.name.trim().length > 0,
    // Either document, on either profile. It used to be the segment that
    // decided: an agency had to produce a CNPJ and a solo broker a CPF,
    // which refused the sole proprietor registering their agency to a CPF
    // and the solo broker who holds a CNPJ — both ordinary.
    document: () => isValidDocument(form.document),
    phone: () => digitCount(form.phone) >= PHONE_MIN_DIGITS,
    // Eight digits are not enough: Payien needs the street, the municipality
    // and its IBGE code, and those only exist once the lookup has answered.
    // A CEP that typed correctly but resolved to nothing still blocks payment.
    cep: () => digitCount(form.cep) === CEP_DIGITS && address != null,
    number: () => form.number.trim().length > 0,
  };
  return REQUIRED_FIELDS.filter((field) => !rules[field]());
}

/**
 * A fingerprint of everything the price depends on.
 *
 * The Payien session freezes a price for fifteen minutes, so any change to
 * what is being bought has to void it and open a new one. Comparing a string
 * is what lets the checkout notice that without tracking each input.
 *
 * @param parts - Plan, cycle, seats and the payer's own details.
 * @returns A key that changes whenever the price or the payer does.
 */
export function sessionKey(parts: {
  planId: string; cycle: BillingCycle; seats: number; form: CheckoutForm;
}): string {
  const { planId, cycle, seats, form } = parts;
  return [planId, cycle, seats, ...Object.values(form)].join('|');
}

/** How many seats of each plan the buyer has chosen, keyed by plan key. */
export type SeatsByPlan = Record<string, number>;

/** One chosen plan, as the checkout sends it. */
export interface CheckoutLine {
  planId: string;
  seats: number;
}

/** The seat floor a subscription has to reach, whatever it is made of. */
export const SUBSCRIPTION_SEAT_FLOOR = 2;

/**
 * The plan id a line has to carry: the one the catalogue answers to.
 *
 * `plan.key` is the base id the monthly and yearly variants share, which is
 * how the seat map is keyed — and which resolves to no plan on the server,
 * because it is neither an id nor a slug. It also drops the cycle, so even a
 * lenient lookup could not tell a yearly purchase from a monthly one.
 */
function catalogueId(plan: PlanWithFrequency, cycle: BillingCycle): string {
  const forCycle = cycle === 'yearly' ? plan.yearlyId : plan.monthlyId;
  return forCycle || plan.originalId || plan.key;
}

/**
 * The lines a seat map amounts to, dropping the plans nobody chose.
 *
 * A counter at zero is a plan the team does not use, not a line worth sending.
 *
 * @param plans - The plans on screen, in display order.
 * @param seatsBy - How many seats of each, keyed by plan key.
 * @param cycle - The billing cycle chosen, which picks the plan variant.
 * @returns One line per chosen plan, in display order.
 */
export function checkoutLines(
  plans: PlanWithFrequency[], seatsBy: SeatsByPlan, cycle: BillingCycle = 'monthly',
): CheckoutLine[] {
  return plans
    .map((plan) => ({
      planId: catalogueId(plan, cycle),
      seats: seatsBy[plan.key] ?? 0,
    }))
    .filter((line) => line.seats > 0);
}

/**
 * The free days a purchase gets, which is the shortest any of its lines gets.
 *
 * Payien takes the `min()` across the lines of a mixed order, so a team that
 * puts one tier without a trial beside one with it pays from day zero. The
 * summary has to say the same number the biller will use, or the screen is a
 * promise nobody is going to keep.
 *
 * @param plans - The plans on screen.
 * @param seatsBy - How many seats of each, keyed by plan key.
 * @param cycle - The billing cycle chosen, which picks the plan variant.
 * @returns The trial in days, or zero when any chosen line grants none.
 */
export function trialDaysFor(
  plans: PlanWithFrequency[], seatsBy: SeatsByPlan, cycle: BillingCycle = 'monthly',
): number {
  const chosen = plans.filter((plan) => (seatsBy[plan.key] ?? 0) > 0);
  if (!chosen.length) return 0;
  return Math.min(...chosen.map((plan) => planTrialDays(plan, cycle)));
}

/**
 * The free days one plan grants on the cycle being bought.
 *
 * @param plan - The plan chosen.
 * @param cycle - The billing cycle chosen.
 * @returns The trial in days, zero when the catalogue sets none.
 */
export function planTrialDays(
  plan: PlanWithFrequency | undefined, cycle: BillingCycle,
): number {
  if (!plan) return 0;
  const forCycle = cycle === 'yearly' ? plan.yearlyTrialDays : plan.monthlyTrialDays;
  return forCycle ?? 0;
}

/**
 * The free days the screen may show: the catalogue offers them, but only an
 * owner the backend confirmed as eligible is promised them (RN-05, AR-07).
 * While the answer is pending, or if it failed, nothing is promised.
 *
 * @param status - What the eligibility lookup says so far.
 * @param catalogueDays - The days the catalogue grants for this order.
 * @returns The days to show, zero for no trial.
 */
export function shownTrialDays(status: TrialStatus, catalogueDays: number): number {
  return status === 'eligible' ? catalogueDays : 0;
}

/**
 * What the chosen seats cost in a month, before the yearly discount.
 *
 * Each plan is priced at its own seat rate, so a team that mixes tiers adds up
 * rather than paying one rate for everyone.
 *
 * @param plans - The plans on screen.
 * @param seatsBy - How many seats of each.
 * @returns The monthly total in currency units.
 */
export function monthlyTotal(
  plans: PlanWithFrequency[], seatsBy: SeatsByPlan,
): number {
  return plans.reduce((total, plan) => {
    const seats = seatsBy[plan.key] ?? 0;
    const rate = plan.monthlyPrice ?? plan.price ?? 0;
    return total + rate * seats;
  }, 0);
}

/**
 * Total seats across every plan.
 *
 * The floor belongs to the subscription, not to any one plan: one Esmeralda
 * seat beside five Rubi is six seats and a team the product sells.
 *
 * @param seatsBy - How many seats of each plan.
 * @returns The sum.
 */
export function totalSeats(seatsBy: SeatsByPlan): number {
  return Object.values(seatsBy).reduce((sum, seats) => sum + (seats || 0), 0);
}

/** One tier's contribution to the auxiliary users the purchase brings. */
export interface SupportSeatLine {
  planKey: string;
  planTitle: string;
  /** Paid seats chosen on this tier. */
  seats: number;
  /** Auxiliary users each of them brings, straight from the catalogue. */
  perSeat: number;
  /** ``seats * perSeat``. */
  total: number;
}

/**
 * How many auxiliary users one tier's seats bring.
 *
 * Read off the plan rather than written down here. The figure is commercial
 * and it has already changed once during this feature's own development; a
 * number typed into a screen is a second answer that stops matching the
 * first the day somebody edits the catalogue.
 */
function supportPerSeat(plan: PlanWithFrequency): number {
  const value = plan.limits?.seat?.support_seats;
  return typeof value === 'number' && value > 0 ? value : 0;
}

/**
 * The auxiliary users a basket of seats brings, per tier and in total.
 *
 * Free, and deliberately not part of any price: nothing here reaches the
 * total, the lines sent to the gateway, or the trial. The subscription
 * already says what was bought, so the figure is derived on read — storing
 * it would be a second answer to the same question, and the two would
 * disagree the first time somebody bought a seat.
 *
 * @param plans - The catalogue, as the checkout holds it.
 * @param seatsBy - Seats chosen per plan key.
 * @returns One line per tier with seats chosen, and the grand total.
 */
export function supportSeats(
  plans: PlanWithFrequency[],
  seatsBy: SeatsByPlan,
): { lines: SupportSeatLine[]; total: number } {
  const lines = plans
    .map((plan) => {
      const seats = seatsBy[plan.key] || 0;
      const perSeat = supportPerSeat(plan);
      return {
        planKey: plan.key,
        planTitle: plan.title,
        seats,
        perSeat,
        total: seats * perSeat,
      };
    })
    .filter((line) => line.total > 0);
  return { lines, total: lines.reduce((sum, line) => sum + line.total, 0) };
}

/** One priced row of the summary: a plan, its seats, what they cost. */
export interface SummaryLine {
  planKey: string;
  planTitle: string;
  seats: number;
  /** What this plan's seats cost over the whole billing period, gross. */
  grossCents: number;
}

/**
 * The summary's plan rows, one per plan the team actually uses.
 *
 * A mixed team is several rows, because "5 cadeiras" over one total says
 * nothing about which tiers those seats are, and the buyer is about to pay for
 * a composition the summary would be hiding.
 *
 * Gross is the period at the monthly rate — twelve months on the yearly plan —
 * so the discount row below can state what the two free months are worth.
 *
 * @param plans - The plans on screen.
 * @param seatsBy - How many seats of each.
 * @param cycle - The billing cycle.
 * @returns One row per chosen plan, in display order.
 */
export function summaryLines(
  plans: PlanWithFrequency[], seatsBy: SeatsByPlan, cycle: BillingCycle,
): SummaryLine[] {
  const months = cycle === 'yearly' ? MONTHS_PER_YEAR : 1;
  return plans
    .map((plan) => ({
      planKey: plan.key,
      planTitle: plan.title,
      seats: seatsBy[plan.key] ?? 0,
      grossCents: (plan.monthlyPrice ?? plan.price ?? 0) * (seatsBy[plan.key] ?? 0) * months,
    }))
    .filter((line) => line.seats > 0);
}
